import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import ClientDashboard from "./pages/ClientDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import BookConsultationPage from "./pages/BookConsultationPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { ConfirmProvider } from "./components/ConfirmDialog";
import { Toaster } from "./components/ui/sonner";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="loading-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm tracking-wide">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/client" replace />;
  return children;
}

function ThemeInit() {
  // Dark mode is the only supported theme. We force the class on mount and
  // clear any stale `ffa-theme=light` left in localStorage from when the
  // toggle existed, so a returning user never sees the unstyled light theme.
  useEffect(() => {
    document.documentElement.classList.add("dark");
    if (localStorage.getItem("ffa-theme") && localStorage.getItem("ffa-theme") !== "dark") {
      localStorage.removeItem("ffa-theme");
    }
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Rescue for a Cloudflare / hosting quirk where a deep-link like
// /reset-password?token=xyz gets redirected to `/?token=xyz` before the SPA
// has a chance to route. If we see a `?token=` query on any pathname other
// than /reset-password, we hard-redirect to the reset page so React Router
// mounts the correct component.
function ResetLinkRescue() {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    if (token && location.pathname !== "/reset-password") {
      window.location.replace(`/reset-password?token=${encodeURIComponent(token)}`);
    }
  }, [location]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <ResetLinkRescue />
        <ThemeInit />
        <ConfirmProvider>
          <Toaster position="top-right" />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/book" element={<BookConsultationPage />} />
            <Route path="/client" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/clients/:clientId" element={<ProtectedRoute adminOnly><ClientDetailPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ConfirmProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
